import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgStyle, NgClass } from '@angular/common';
import { DatePipe } from '@angular/common';

import { RecordingsService } from '../../services/recordings.service';
import { Recording, RecordStatus, RecordingCl } from '../../data-models/recording.typings';
import { ModalsService, ConfirmableActions, DialogAction } from '../../services/modals.service';
import { AuthentificationService } from '../../services/authentification.service';
import { SortingService } from '../../services/sorting.service';

import { ColInfo, SortingState } from '../../data-models/table.typings';
import { colS } from './tables/recordings';
import { isNull } from 'util';


@Component({
  selector: 'app-my-records',
  templateUrl: './myRecordings.component.html',
  styleUrls: ['./myRecordings.component.scss']
})
export class MyRecordingsComponent implements OnInit, OnDestroy {
  // --------- Constants ---------
  private HOURS_IN_MS = 3600 * 1000;
  private tableCols = colS;
  private currentSorting = this.tableCols.find(col => col.name === 'Date');
  // --------- Recordings ---------
  private recordingS: RecordingCl[];
  private selectedRecordings: RecordingCl[] = [];
  private isInternalChange = false;
  private isAllSelected = false;
  // --------- Revealers ---------
  private isSearcherOpened = false;
  private isLoadingFinished = false;
  private isRendered = false;

  private lastSearchBy: ColInfo;
  private downloadLinks: HTMLElement[];

  @ViewChild('tableHeaderNative') private theadNat: ElementRef;
  @ViewChild('tableHeaderFixed') private theadFix: ElementRef;

  constructor(
    private recordingsDB: RecordingsService,
    private auth: AuthentificationService,
    private modals: ModalsService,
    private sorter: SortingService,
    private datePipe: DatePipe,
  ) { }

  ngOnInit() {
    if (this.auth.maintainUserSession()) {
      this.modals.openLoadingScreen();
    }
    this.recordingsDB.recordings.subscribe(this.setRecordings);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.getTableHeadSizing);
  }

  setRecordings = (recordingS: RecordingCl[], isInternal = false) => {
    this.recordingS = recordingS;
    this.isLoadingFinished = true;
    setTimeout(() => {
      this.isRendered = true;
      this.modals.closeLoadingScreen();
      this.maintainFixedTableHeader();
      this.downloadLinks = Array.from(document.getElementsByClassName('download-link')) as HTMLElement[];
    }, 500);
    this.selectedRecordings = [];
    this.isInternalChange = isInternal;

    if (this.lastSearchBy) {
      this.search(this.lastSearchBy.search.lastSearchBy, this.lastSearchBy);
    }
    this.setSort();
  }



  // ----------- Data transformers -----------
  getDuration(recording: Recording): string {
    let response: string;

    if (recording.durationInMS < this.HOURS_IN_MS) {
      response = this.datePipe.transform(recording.durationInMS, 'mm:ss');
    } else {
      response = this.datePipe.transform(recording.durationInMS, 'HH:mm:ss');
    }

    return response;
  }


  getDate(recording: Recording): string {
    let result: string;
    if (!isNull(recording.timestamp)) {
      result = new Date(recording.timestamp).toLocaleDateString();
    } else {
      result = '';
    }
    return result;
  }

  getTime(recording: Recording): string {
    let result: string;
    if (!isNull(recording.timestamp)) {
      result = new Date(recording.timestamp).toLocaleTimeString();
      if (result[1] < '0' || result[1] > '9') {
        result = '0' + result;
      }
    } else {
      result = '';
    }
    return result;
  }

  getStatus(recording: Recording): string {
    let res = recording.status.charAt(0).toUpperCase() + recording.status.slice(1);

    if (recording.status === RecordStatus.InProgress) {
      res += '...';
    }

    return res;
  }

  // ----------- Styles setter -----------
  getColStyle(col: ColInfo): any {
    return { 'width.px': col.width };
  }

  // ----------- Selection controller -----------
  onSelection(isSelected: boolean, recording: RecordingCl) {
    recording.appInfo.selected = isSelected;

    if (isSelected) {
      this.selectedRecordings.push(recording);
    } else {
      this.selectedRecordings = this.selectedRecordings.filter(selectedRecord => selectedRecord.recordingId !== recording.recordingId);
    }

    if (this.selectedRecordings.length === this.recordingS.length) {
      this.isAllSelected = true;
    } else {
      this.isAllSelected = false;
    }
  }


  onSelectionAll(isAllSelected: boolean) {
    this.isAllSelected = isAllSelected;

    if (isAllSelected) {
      this.recordingS.forEach(recording => { recording.appInfo.selected = true; });
      this.selectedRecordings = this.recordingS;
    } else {
      this.recordingS.forEach(recording => { recording.appInfo.selected = false; });
      this.selectedRecordings = [];
    }
  }

  isNoneSelected(): boolean {
    return this.selectedRecordings.length === 0;
  }

  // ----------- Downloaders -----------
  downloadSelected() {
    // console.log(this.downloadLinks);

    this.selectedRecordings.forEach((rec, index) => {
      const ref = this.downloadLinks.find(link => link.id === rec.recordingId);
      // console.log(ref);
      setTimeout(() => ref.click(), (1000 * index));
    });
  }

  getUrl(recording: Recording): string {
    return this.recordingsDB.getDownloadUrl(recording);
  }
  // ----------- Player controllers -----------
  openPlayer(record: Recording) {
    this.modals.openPlayer(record.recordingId);
  }

  // ----------- Visibility controllers -----------
  isRecorded(recording: Recording): boolean {
    return recording.status === RecordStatus.Recorded;
  }

  isTableReady(): boolean {
    return this.isLoadingFinished && (this.recordingS.length > 0 || this.isInternalChange);
  }

  isLoading(): boolean {
    return !this.isLoadingFinished;
  }

  isNoData(): boolean {
    return this.isLoadingFinished && this.recordingS.length === 0 && !this.isInternalChange;
  }
  // ----------- Data modifires -----------
  delete(recording: Recording) {
    this.modals.openWarning(
      'Delete',
      'Are you sure you want to delete this recording?',
      ConfirmableActions.Delete
    ).then((isAgreed: boolean) => {
      if (isAgreed) {
        this.recordingsDB.delete(recording);
      }
    });
  }


  deleteSelected() {
    const isMany = this.selectedRecordings.length > 1 ? 's?' : '?';

    this.modals.openWarning(
      'Delete',
      'Are you sure you want to delete ' + this.selectedRecordings.length + ' recording' + isMany,
      ConfirmableActions.DeleteSelected
    ).then((isAgreed: boolean) => {
      if (isAgreed) {
        this.recordingsDB.deleteSelected(this.selectedRecordings);
        this.isAllSelected = false;
      }
    });
  }


  onTitleChange(newTitle: string, recording: Recording) {
    if (recording.title !== newTitle) {
      this.recordingsDB.changeTitle(newTitle, recording);
    }
  }


  onCommentChange(newComment: string, recording: Recording) {
    if (recording.transcript !== newComment) {
      this.recordingsDB.changeComment(newComment, recording);
    }
  }

  // ----------- Sorting -----------
  setSort() {
    const sortCol = this.tableCols.find(col => col.name === this.currentSorting.name);

    this.maintainSorting(sortCol, this.isInternalChange);
    this.sorting(sortCol);
  }

  sorting(col: ColInfo) {
    if (this.currentSorting.name !== col.name) {
      this.currentSorting.sort.state = SortingState.None;
      this.currentSorting = col;
    }

    if (col.sort.state === SortingState.None) {
      col.sort.state = SortingState.Increasing;
    }

    switch (col.sort.state) {
      case SortingState.Decresing:
        this.recordingS = this.sorter.increasing(this.recordingS, col.key);
        col.sort.state = SortingState.Increasing;
        break;
      case SortingState.Increasing:
        this.recordingS = this.sorter.decreasing(this.recordingS, col.key);
        col.sort.state = SortingState.Decresing;
        break;
    }

    this.recordingsDB.lastSortedrecordings = this.recordingS;
  }

  maintainSorting(col: ColInfo, isInternal: boolean) {
    switch (col.sort.state) {
      case SortingState.Decresing: col.sort.state = SortingState.Increasing; break;
      case SortingState.Increasing: col.sort.state = SortingState.Decresing; break;
    }
  }

  upSorting(col: ColInfo): any {
    return {
      'sort-up-icon': col.sort.state === SortingState.None || !(col.sort.state === SortingState.Increasing),
      'sort-up-sel-icon': col.sort.state === SortingState.Increasing
    };
  }

  downSorting(col: ColInfo): any {
    return {
      'sort-down-icon': col.sort.state === SortingState.None || !(col.sort.state === SortingState.Decresing),
      'sort-down-sel-icon': col.sort.state === SortingState.Decresing
    };
  }
  // ----------- Searcher -----------
  openSearcher() {
    this.isSearcherOpened = true;
  }

  closeSearcher() {
    this.isSearcherOpened = false;
  }

  toggleSearcher() {
    if (this.isSearcherOpened) {
      this.closeSearcher();
    } else {
      this.openSearcher();
    }
  }

  search(searchable: string, col: ColInfo) {
    this.cancelSearch(col);
    col.search.lastSearchBy = searchable;
    searchable = searchable.toLowerCase();

    const field = col.key;

    this.isInternalChange = true;
    if (searchable.length) {
      this.recordingS = this.recordingS.filter(rec => rec[field].toLowerCase().includes(searchable));
      this.lastSearchBy = col;
    }

    setTimeout(() => {
      if (this.recordingS.length) {
        this.getTableHeadSizing();
      } else {
        this.maintainPhantomHeader();
      }
    });
    this.recordingsDB.lastSortedrecordings = this.recordingS;
  }

  cancelSearch(col: ColInfo) {
    col.search.lastSearchBy = '';
    this.recordingS = this.recordingsDB.getPureLastDataIncome();
    this.setSort();
    this.lastSearchBy = null;
  }
  // ----------- Other -----------
  push() {
    for (let i = 0; i < 5; i++) {
      this.recordingsDB.push();
    }
  }

  getTotalWidth(): string {
    let response = 225;

    this.tableCols.forEach(col => {
      response += col.width;
    });

    return response + 'px';
  }


  getTableHeadSizing() {
    const theadNat = document.getElementById('TableHeaderNative');
    const theadFix = document.getElementById('TableHeaderFixed');
    const caption = document.getElementById('Caption');
    const table = document.getElementById('Table');

    if (theadNat && theadFix && table.offsetWidth >= 840) {
      const colsNat = Array.from(theadNat.getElementsByTagName('td'));
      const colsFix = Array.from(theadFix.getElementsByTagName('td'));
      let sum = 5;

      colsNat.forEach((col, index) => {
        colsFix[index].style.width = col.offsetWidth + 'px';
        colsFix[index].style.minWidth = col.offsetWidth + 'px';
        sum += col.offsetWidth;
      });

      caption.style.width = sum + 'px';
    } else {
      // console.error('Table headers were not found in the DOM by some reason');
    }
  }

  maintainPhantomHeader() {
    const theadNat = document.getElementById('TableHeaderNative');
    const theadFix = document.getElementById('TableHeaderFixed');

    if (theadNat && theadFix) {
      const colsNat = Array.from(theadNat.getElementsByTagName('td'));
      const colsFix = Array.from(theadFix.getElementsByTagName('td'));

      colsFix.forEach((col, index) => {
        colsNat[index].style.width = col.offsetWidth + 'px';
      });

    } else {
      // console.error('Table headers were not found in the DOM by some reason');
    }
  }

  maintainFixedTableHeader() {
    this.getTableHeadSizing();
    window.removeEventListener('resize', this.getTableHeadSizing);
    window.addEventListener('resize', this.getTableHeadSizing);
  }
}



// WEBPACK FOOTER //
// ./src/app/main/myRecordings/myRecordings.component.ts